// ─────────────────────────────────────────────────────────────
//  Academy Members data layer (Realtime Database + Cloud Functions)
//
//  Mirrors the Research access pattern, but member-centric: every member owns a
//  unique secret key that unlocks their personal Academy + progress vault.
//
//  NOTE: filename intentionally differs from the `AcademyMembers.js` component
//  by more than just case — the project lives on a case-insensitive filesystem,
//  so `academyMembers.js` + `AcademyMembers.js` would collide.
//
//  Storage (under salesgod-research/…):
//    academyMembers/{memberId} : { name, email, note, active, createdAt, … }   (admin read/write)
//    academySecrets/{memberId} : { key }                                       (read:false → key via Cloud Function)
//    academyProgress/{memberId}: { lessons: { topic: { course: { lesson: { watched, quizDone, practiceDone, submission } } } }, lastActiveAt }
//
//  Admin reads members + progress directly (admin-authed). Secret keys are only
//  ever returned by the admin-only `getAcademyKeys` Cloud Function. End users
//  authenticate + persist progress exclusively through `academyLogin` /
//  `saveAcademyProgress` (admin SDK), never via direct DB writes.
// ─────────────────────────────────────────────────────────────
import { ref, get, set, update, remove, onValue } from 'firebase/database';
import { database, auth } from '../firebaseConfig';
import { slugify } from './academyDb';

const MEMBERS_ROOT = 'salesgod-research/academyMembers';
const SECRETS_ROOT = 'salesgod-research/academySecrets';
const PROGRESS_ROOT = 'salesgod-research/academyProgress';

const FN_BASE = 'https://us-central1-bc-landingpage.cloudfunctions.net';
export const ACADEMY_LOGIN_URL = `${FN_BASE}/academyLogin`;
export const ACADEMY_SAVE_PROGRESS_URL = `${FN_BASE}/saveAcademyProgress`;
const ACADEMY_KEYS_URL = `${FN_BASE}/getAcademyKeys`;
const ACADEMY_PROFILE_URL = `${FN_BASE}/academyUpdateProfile`;

// ── Secret key generator ──
// Mirrors the backend format: 4 groups of 4 chars from an unambiguous alphabet
// (no 0/O, 1/I/l). e.g. "K7MR-P2QW-9TZX-A4VH". Uses the Web Crypto CSPRNG.
const KEY_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
export const genAcademyKey = () => {
  const groups = [];
  const rnd = new Uint32Array(16);
  (window.crypto || window.msCrypto).getRandomValues(rnd);
  let idx = 0;
  for (let g = 0; g < 4; g += 1) {
    let part = '';
    for (let i = 0; i < 4; i += 1) {
      part += KEY_ALPHABET[rnd[idx++] % KEY_ALPHABET.length];
    }
    groups.push(part);
  }
  return groups.join('-');
};

// ── Live subscriptions (admin) ──
const membersToArray = (raw) =>
  raw && typeof raw === 'object'
    ? Object.entries(raw)
        .map(([id, value]) => ({ id, ...value }))
        .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
    : [];

export const subscribeMembers = (cb) =>
  onValue(ref(database, MEMBERS_ROOT), (snap) => cb(membersToArray(snap.val())));

export const subscribeAllProgress = (cb) =>
  onValue(ref(database, PROGRESS_ROOT), (snap) => cb(snap.val() || {}));

// ── Member CRUD (admin, direct RTDB writes) ──
const newMemberId = async (name) => {
  const base = slugify(name) || `member-${Date.now().toString(36)}`;
  const snap = await get(ref(database, MEMBERS_ROOT));
  const existing = snap.val() ? Object.keys(snap.val()) : [];
  if (!existing.includes(base)) return base;
  let n = 2;
  while (existing.includes(`${base}-${n}`)) n += 1;
  return `${base}-${n}`;
};

// Creates a member + secret key. Name/email are optional — a member can be
// created with just a key; the user fills in name/email on first login.
// Returns { id, key }.
export const createMember = async ({ name = '', email = '', note = '' } = {}) => {
  const clean = (name || '').trim();
  const id = await newMemberId(clean);
  const key = genAcademyKey();
  const now = Date.now();
  await set(ref(database, `${MEMBERS_ROOT}/${id}`), {
    name: clean,
    email: (email || '').trim(),
    note: (note || '').trim(),
    active: true,
    createdBy: auth.currentUser?.email || 'unknown',
    createdAt: now,
    updatedAt: now,
  });
  await set(ref(database, `${SECRETS_ROOT}/${id}`), { key });
  return { id, key };
};

export const updateMember = (id, patch) =>
  update(ref(database, `${MEMBERS_ROOT}/${id}`), { ...patch, updatedAt: Date.now() });

// ── Course access ──
// Access is a flat map keyed by `${topicId}__${courseId}` → true. The separator
// avoids the "/" RTDB forbids in keys (topic/course ids are slugs, no "__").
export const accessKey = (topicId, courseId) => `${topicId}__${courseId}`;
export const hasCourseAccess = (accessMap, topicId, courseId) =>
  !!(accessMap && accessMap[accessKey(topicId, courseId)]);

// Replaces the whole access map for a member. Pass only the unlocked courses
// (a key present + true = unlocked). An empty map locks everything.
export const setMemberAccess = (id, accessMap) =>
  update(ref(database, `${MEMBERS_ROOT}/${id}`), {
    access: accessMap && Object.keys(accessMap).length ? accessMap : null,
    updatedAt: Date.now(),
  });

export const setMemberActive = (id, active) =>
  update(ref(database, `${MEMBERS_ROOT}/${id}`), { active: !!active, updatedAt: Date.now() });

// Generates a fresh key (invalidates the old one). Returns the new key.
export const regenerateKey = async (id) => {
  const key = genAcademyKey();
  await set(ref(database, `${SECRETS_ROOT}/${id}`), { key });
  await update(ref(database, `${MEMBERS_ROOT}/${id}`), { updatedAt: Date.now() });
  return key;
};

export const deleteMember = async (id) => {
  await remove(ref(database, `${MEMBERS_ROOT}/${id}`));
  await remove(ref(database, `${SECRETS_ROOT}/${id}`));
  await remove(ref(database, `${PROGRESS_ROOT}/${id}`));
};

// ── Secret keys (admin Cloud Function — academySecrets is read:false) ──
export const fetchMemberKeys = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error('Nicht angemeldet.');
  const token = await user.getIdToken();
  const res = await fetch(ACADEMY_KEYS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: '{}',
  });
  let payload = {};
  try {
    payload = await res.json();
  } catch (e) {
    payload = {};
  }
  if (!res.ok || !payload.ok) throw new Error(payload.error || `HTTP ${res.status}`);
  return payload.keys || {};
};

// ── Member-facing access (no Firebase Auth — secret key only) ──
// Validates the key and returns { member, progress } where progress is the
// nested { topic: { course: { lesson: {...} } } } map.
export const academyLogin = async (key) => {
  const res = await fetch(ACADEMY_LOGIN_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key }),
  });
  let payload = {};
  try {
    payload = await res.json();
  } catch (e) {
    payload = {};
  }
  if (!res.ok || !payload.ok) {
    const err = new Error(payload.error || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return { member: payload.member, progress: payload.progress || {} };
};

// Member self-service: set their own name/email on first login (key-scoped).
// Returns the updated member { id, name, email, access }.
export const updateProfile = async ({ key, name, email, whatsapp }) => {
  const res = await fetch(ACADEMY_PROFILE_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key, name, email, whatsapp }),
  });
  let payload = {};
  try {
    payload = await res.json();
  } catch (e) {
    payload = {};
  }
  if (!res.ok || !payload.ok) {
    const err = new Error(payload.error || `HTTP ${res.status}`);
    err.status = res.status;
    throw err;
  }
  return payload.member;
};

// Persists a single completed step for the member behind `key`.
// kind ∈ 'watched' | 'quizDone' | 'practiceDone'.
// `submission` (optional) stores the actual content the member submitted
// (practice-lab text / quiz answers) — only the latest is kept per exercise.
export const saveProgress = async ({ key, topicId, courseId, lessonId, kind, submission }) => {
  const res = await fetch(ACADEMY_SAVE_PROGRESS_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key, topicId, courseId, lessonId, kind, submission }),
  });
  let payload = {};
  try {
    payload = await res.json();
  } catch (e) {
    payload = {};
  }
  if (!res.ok || !payload.ok) throw new Error(payload.error || `HTTP ${res.status}`);
  return true;
};

// Flattens the nested progress map from the DB/login into the flat shape the
// AcademyContext uses: { "topicId/courseId/lessonId": { watched, quizDone, … } }.
export const flattenProgress = (nested) => {
  const flat = {};
  if (!nested || typeof nested !== 'object') return flat;
  Object.entries(nested).forEach(([topicId, courses]) => {
    if (!courses || typeof courses !== 'object') return;
    Object.entries(courses).forEach(([courseId, lessons]) => {
      if (!lessons || typeof lessons !== 'object') return;
      Object.entries(lessons).forEach(([lessonId, state]) => {
        if (!state || typeof state !== 'object') return;
        flat[`${topicId}/${courseId}/${lessonId}`] = {
          watched: !!state.watched,
          quizDone: !!state.quizDone,
          practiceDone: !!state.practiceDone,
          submission: state.submission || null,
          submittedAt: state.submittedAt || 0,
        };
      });
    });
  });
  return flat;
};
